import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ArrowLeft, Save, Trash2, Plus, Send, Image as ImageIcon, MessageCircle, Beaker } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const KIND_COLOR = {
  otp: "bg-blue-100 text-blue-800",
  invoice: "bg-green-100 text-green-800",
  booking: "bg-purple-100 text-purple-800",
  reminder: "bg-orange-100 text-orange-800",
  receipt: "bg-amber-100 text-amber-800",
  boarding: "bg-cyan-100 text-cyan-800",
  template_test: "bg-amber-100 text-amber-800",
  probe: "bg-slate-100 text-slate-800",
  generic: "bg-gray-100 text-gray-800",
};

export default function WhatsAppManage() {
  const navigate = useNavigate();
  return (
    <div className="space-y-5" data-testid="whatsapp-manage-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <button
            onClick={() => navigate("/settings")}
            className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="back-to-settings"
          >
            <ArrowLeft className="w-3 h-3" /> Settings
          </button>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Manage WhatsApp</h1>
          <p className="text-sm text-muted-foreground mt-1">Integration, message templates, and send logs — all in one place.</p>
        </div>
      </div>

      <Tabs defaultValue="templates" className="space-y-4">
        <TabsList data-testid="wa-tabs">
          <TabsTrigger value="integration" data-testid="tab-integration">Integration</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="integration"><IntegrationTab /></TabsContent>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
        <TabsContent value="logs"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Integration tab (Wabis credentials) ---------- */
function IntegrationTab() {
  const [wabis, setWabis] = useState(null);
  const [tokenDraft, setTokenDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [testNum, setTestNum] = useState("");
  const [probeResult, setProbeResult] = useState(null);
  const [mode, setMode] = useState("mock");

  const reload = () => Promise.all([
    api.get("/settings/wabis").then(r => { setWabis(r.data); setMode(r.data.mode); }),
  ]);
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      // Only send access_token if admin actually typed a new one — empty draft
      // means "keep the saved value".
      const payload = {
        enabled: wabis.enabled,
        api_url: wabis.api_url || "",
        phone_number_id: wabis.phone_number_id || "",
        business_name: wabis.business_name || "",
        sender_display_name: wabis.sender_display_name || "",
      };
      if (tokenDraft.trim()) payload.access_token = tokenDraft.trim();
      await api.patch("/settings/wabis", payload);
      setTokenDraft("");
      toast.success("Saved");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const clearToken = async () => {
    setBusy(true);
    try {
      await api.delete("/settings/wabis/token");
      toast.success("Token cleared");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const probe = async () => {
    setBusy(true);
    setProbeResult(null);
    try {
      const { data } = await api.post("/settings/wabis/test", null, {
        params: testNum ? { test_recipient: testNum } : {},
      });
      setProbeResult(data);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Probe failed"); }
    finally { setBusy(false); }
  };

  if (!wabis) return <Card className="p-6">Loading…</Card>;

  const tokenPlaceholder = wabis.has_token
    ? `Saved (${wabis.access_token_masked}) — paste a new token to replace`
    : "Paste your Wabis apiToken (from bot.wabis.in → Developer)";

  return (
    <Card className="p-6 space-y-5" data-testid="integration-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading text-xl font-semibold flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            Wabis WhatsApp credentials
          </div>
          <div className="text-sm text-muted-foreground">Saved per environment. Each deployment keeps its own creds — set them once and they persist.</div>
        </div>
        <Badge className={mode === "live" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"} data-testid="integration-mode">
          {mode.toUpperCase()} mode
        </Badge>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>API URL</Label>
          <Input
            placeholder="https://bot.wabis.in"
            value={wabis.api_url || ""}
            onChange={e => setWabis({ ...wabis, api_url: e.target.value })}
            data-testid="wabis-api-url"
          />
        </div>
        <div>
          <Label>API Token</Label>
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder={tokenPlaceholder}
              value={tokenDraft}
              onChange={e => setTokenDraft(e.target.value)}
              data-testid="wabis-token"
            />
            {wabis.has_token && (
              <Button type="button" variant="outline" onClick={clearToken} disabled={busy} data-testid="wabis-clear-token">
                Clear
              </Button>
            )}
          </div>
          {wabis.has_token && !tokenDraft && (
            <div className="text-xs text-emerald-700 mt-1">✓ Token saved · {wabis.access_token_masked}</div>
          )}
        </div>
        <div>
          <Label>Phone Number ID</Label>
          <Input
            placeholder="15-digit ID from Wabis dashboard"
            value={wabis.phone_number_id || ""}
            onChange={e => setWabis({ ...wabis, phone_number_id: e.target.value })}
            data-testid="wabis-phone-id"
          />
        </div>
        <div>
          <Label>Display name (optional)</Label>
          <Input
            placeholder="Furmigo"
            value={wabis.sender_display_name || ""}
            onChange={e => setWabis({ ...wabis, sender_display_name: e.target.value })}
            data-testid="wabis-display-name"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          checked={!!wabis.enabled}
          onCheckedChange={(v) => setWabis({ ...wabis, enabled: v })}
          data-testid="wabis-enabled"
        />
        <Label>Enable live sending</Label>
      </div>

      <div className="flex flex-wrap gap-3 items-end pt-2 border-t border-border">
        <Button onClick={save} disabled={busy} data-testid="wabis-save">
          <Save className="w-4 h-4 mr-1" />{busy ? "Saving…" : "Save credentials"}
        </Button>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Send test to (optional)</Label>
            <Input
              placeholder="9876543210"
              value={testNum}
              onChange={e => setTestNum(e.target.value)}
              className="w-44"
              data-testid="wabis-test-recipient"
            />
          </div>
          <Button variant="outline" onClick={probe} disabled={busy} data-testid="wabis-test-connection">
            <Beaker className="w-4 h-4 mr-1" />Test connection
          </Button>
        </div>
      </div>

      {probeResult && (
        <div className={`text-xs rounded p-3 ${probeResult.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"} border`} data-testid="wabis-probe-result">
          <div className="font-semibold">{probeResult.ok ? "✓ Connected" : "✗ Failed"}</div>
          <div className="mt-1">{probeResult.message}</div>
        </div>
      )}
    </Card>
  );
}

/* ---------- Templates tab ---------- */
function TemplatesTab() {
  const [tpls, setTpls] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/settings/whatsapp/templates").then(r => {
    setTpls(r.data);
    setSelected(prev => prev ? r.data.find(t => t.kind === prev.kind) : r.data[0]);
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!tpls.length) return <Card className="p-6">Loading…</Card>;

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-4" data-testid="templates-tab">
      {/* Kind list */}
      <Card className="p-2 h-fit lg:sticky lg:top-4">
        <div className="space-y-1">
          {tpls.map(t => (
            <button
              key={t.kind}
              onClick={() => setSelected(t)}
              className={`w-full text-left px-3 py-2.5 rounded transition ${selected?.kind === t.kind ? "bg-primary/10 text-foreground" : "hover:bg-muted"}`}
              data-testid={`tpl-pick-${t.kind}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm">{t.label}</span>
                {!t.enabled && <Badge variant="outline" className="text-[10px]">Off</Badge>}
              </div>
              <div className="text-xs text-muted-foreground truncate">{t.description}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Editor */}
      {selected && <TemplateEditor key={selected.kind} initial={selected} onSaved={load} />}
    </div>
  );
}

function TemplateEditor({ initial, onSaved }) {
  const [body, setBody] = useState(initial.body || "");
  const [enabled, setEnabled] = useState(initial.enabled !== false);
  const [links, setLinks] = useState(initial.extra_links || []);
  const [mediaPath, setMediaPath] = useState(initial.media_image_path || "");
  const [imageUrl, setImageUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testNum, setTestNum] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [botFlowDiag, setBotFlowDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [botFlowId, setBotFlowId] = useState(initial.bot_flow_unique_id || "");
  const [botFlowMap, setBotFlowMap] = useState(
    Object.entries(initial.bot_flow_field_map || {}).map(([k, v]) => ({ wabis: k, var: v }))
  );
  const fileRef = React.useRef(null);

  useEffect(() => {
    // Render preview using the existing public-files endpoint. We deliberately
    // do NOT build URLs in the frontend that hardcode the preview domain — the
    // backend builds the URL using merchant.storefront_base_url.
    if (mediaPath) {
      api.get(`/settings/whatsapp/templates/${initial.kind}`)
        .then(r => setImageUrl(r.data.image_preview_url || ""))
        .catch(() => setImageUrl(""));
    } else { setImageUrl(""); }
  }, [mediaPath, initial.kind]);

  const save = async () => {
    setBusy(true);
    try {
      const mapObj = {};
      botFlowMap.forEach(p => { if (p.wabis && p.var) mapObj[p.wabis.trim()] = p.var.trim(); });
      await api.patch(`/settings/whatsapp/templates/${initial.kind}`, {
        body, enabled, extra_links: links.filter(l => l.url),
        bot_flow_unique_id: botFlowId.trim(),
        bot_flow_field_map: mapObj,
      });
      toast.success("Template saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/settings/whatsapp/templates/${initial.kind}/image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMediaPath(data.media_image_path);
      toast.success("Image uploaded");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = async () => {
    setBusy(true);
    try {
      await api.delete(`/settings/whatsapp/templates/${initial.kind}/image`);
      setMediaPath("");
      toast.success("Image removed");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testNum) { toast.error("Enter a phone number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/settings/whatsapp/templates/${initial.kind}/test`, null, {
        params: { test_recipient: testNum },
      });
      setTestResult(data);
      if (data.status === "sent") toast.success("Test sent to " + testNum);
      else toast.error("Send returned: " + data.status);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };

  const diagnoseBotFlow = async () => {
    if (!testNum) { toast.error("Enter a phone number in the Bot Flow section below"); return; }
    if (!botFlowId.trim()) { toast.error("Save a Bot Flow Unique ID first"); return; }
    setDiagBusy(true);
    setBotFlowDiag(null);
    try {
      const { data } = await api.post(
        `/settings/whatsapp/templates/${initial.kind}/test-bot-flow`,
        null, { params: { test_recipient: testNum } },
      );
      setBotFlowDiag(data);
      if (data.ok) toast.success("Bot Flow triggered successfully");
      else toast.error(data.message || "Bot Flow failed — see step details");
    } catch (e) { toast.error(e?.response?.data?.detail || "Diagnostic failed"); }
    finally { setDiagBusy(false); }
  };

  return (
    <Card className="p-6 space-y-5" data-testid={`tpl-editor-${initial.kind}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-heading text-2xl font-semibold">{initial.label}</div>
          <div className="text-sm text-muted-foreground">{initial.description}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="tpl-enabled" />
            <Label className="text-sm">Enabled</Label>
          </div>
          <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="tpl-test-btn">
            <Send className="w-4 h-4 mr-1" />Send test
          </Button>
          <Button onClick={save} disabled={busy} data-testid="tpl-save">
            <Save className="w-4 h-4 mr-1" />{busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* Body editor */}
      <div>
        <Label className="text-sm">Message body</Label>
        <Textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={10}
          className="font-mono text-xs mt-1"
          placeholder="Hi {{customer_name}}! ..."
          data-testid="tpl-body"
        />
        <div className="text-xs text-muted-foreground mt-2">
          Available variables:{" "}
          {initial.variables.map(v => (
            <code
              key={v}
              className="inline-block bg-muted rounded px-1.5 py-0.5 mr-1 mb-1 text-[10px] cursor-pointer hover:bg-primary/10"
              onClick={() => setBody(prev => prev + `{{${v}}}`)}
              data-testid={`tpl-var-${v}`}
              title="Click to insert"
            >{`{{${v}}}`}</code>
          ))}
        </div>
      </div>

      {/* Header image */}
      <div>
        <Label className="text-sm flex items-center gap-2"><ImageIcon className="w-4 h-4" />Header image (optional)</Label>
        {mediaPath ? (
          <div className="mt-2 rounded border border-border bg-muted/30 p-2 flex items-start gap-3">
            {imageUrl ? (
              <img src={imageUrl} alt="Template header" className="w-32 h-32 object-cover rounded" data-testid="tpl-img-preview" />
            ) : (
              <div className="w-32 h-32 flex items-center justify-center bg-muted rounded text-xs text-muted-foreground">Image loaded</div>
            )}
            <div className="flex-1 flex flex-col gap-2">
              <div className="text-xs text-muted-foreground">Sent as binary to Wabis. No public URL exposed.</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="tpl-img-replace">Replace</Button>
                <Button size="sm" variant="ghost" onClick={removeImage} disabled={busy} data-testid="tpl-img-remove"><Trash2 className="w-3 h-3 mr-1" />Remove</Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-3">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="tpl-img-upload">
              Upload (JPG/PNG/WEBP, ≤ 5 MB)
            </Button>
            <span className="text-xs text-muted-foreground">Optional — sent as binary to Wabis.</span>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={e => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="tpl-img-input"
        />
      </div>

      {/* Wabis Bot Flow trigger (sends APPROVED Meta template — works for ANY customer) */}
      <div className="rounded border border-primary/30 bg-primary/5 p-4 space-y-3">
        <div>
          <Label className="text-sm font-semibold flex items-center gap-2">
            <Send className="w-4 h-4 text-primary" />
            Wabis Bot Flow (sends approved Meta template)
          </Label>
          <div className="text-xs text-muted-foreground mt-1">
            When set, the message is sent via Wabis Bot Flow → fires your approved Meta template (e.g. <code className="bg-muted px-1 rounded">furmigo_invoice</code> with View Bill + Rate Us buttons). <strong>This is the only way to message brand-new customers</strong> outside the 24-hour window.
          </div>
        </div>
        <div>
          <Label className="text-xs">Bot Flow Unique ID</Label>
          <Input
            value={botFlowId}
            onChange={e => setBotFlowId(e.target.value)}
            placeholder="e.g. 1878601 (from Wabis → Bot Manager → Bot Reply → Unique ID column)"
            data-testid="tpl-botflow-id"
          />
        </div>
        {botFlowId && (
          <div>
            <Label className="text-xs">Field mapping (Wabis Custom Field → ERP variable)</Label>
            <div className="text-[11px] text-muted-foreground mb-2">
              Map each Wabis Custom Field (left, exactly as named in Wabis → Bot Manager → Custom Fields) to the ERP template variable (right) whose value should be sent.
            </div>
            <div className="space-y-2">
              {botFlowMap.map((p, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`tpl-botflow-map-${i}`}>
                  <Input
                    placeholder="Wabis field (e.g. name)"
                    value={p.wabis}
                    onChange={e => setBotFlowMap(botFlowMap.map((x, j) => j === i ? { ...x, wabis: e.target.value } : x))}
                    className="flex-1"
                    data-testid={`tpl-botflow-wabis-${i}`}
                  />
                  <span className="text-xs text-muted-foreground">→</span>
                  <select
                    value={p.var}
                    onChange={e => setBotFlowMap(botFlowMap.map((x, j) => j === i ? { ...x, var: e.target.value } : x))}
                    className="h-10 px-3 rounded-md border border-input bg-background text-sm flex-1"
                    data-testid={`tpl-botflow-var-${i}`}
                  >
                    <option value="">-- select variable --</option>
                    {initial.variables.map(v => <option key={v} value={v}>{`{{${v}}}`}</option>)}
                  </select>
                  <Button variant="ghost" size="icon" onClick={() => setBotFlowMap(botFlowMap.filter((_, j) => j !== i))} data-testid={`tpl-botflow-del-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setBotFlowMap([...botFlowMap, { wabis: "", var: "" }])} data-testid="tpl-botflow-add">
                <Plus className="w-4 h-4 mr-1" />Add field mapping
              </Button>
            </div>
          </div>
        )}
        {botFlowId && (
          <div className="border-t border-primary/20 pt-3 space-y-2">
            <Label className="text-xs font-semibold">Test this Bot Flow end-to-end</Label>
            <div className="text-[11px] text-muted-foreground">
              Runs all 3 Wabis calls (subscriber create → assign custom fields → trigger bot) and shows the raw response of each step. Save the template first so the latest Bot Flow ID & field mapping are used.
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Input
                placeholder="Recipient phone (e.g. 9876543210)"
                value={testNum}
                onChange={e => setTestNum(e.target.value)}
                className="flex-1 min-w-[200px]"
                data-testid="tpl-botflow-test-recipient"
              />
              <Button
                variant="outline"
                onClick={diagnoseBotFlow}
                disabled={diagBusy}
                data-testid="tpl-botflow-test-btn"
              >
                <Beaker className="w-4 h-4 mr-1" />{diagBusy ? "Running…" : "Test Bot Flow"}
              </Button>
            </div>
            {botFlowDiag && (
              <div
                className={`text-xs rounded p-2.5 space-y-2 ${botFlowDiag.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"} border`}
                data-testid="tpl-botflow-diag-result"
              >
                <div className="font-mono">
                  {botFlowDiag.ok ? "✓" : "✗"} {botFlowDiag.message}
                </div>
                {botFlowDiag.custom_fields_sent && (
                  <div className="text-[11px]">
                    <div className="font-semibold">Custom fields sent:</div>
                    <pre className="bg-white/60 rounded p-1.5 overflow-auto text-[10px]">{JSON.stringify(botFlowDiag.custom_fields_sent, null, 2)}</pre>
                  </div>
                )}
                {(botFlowDiag.steps || []).map((s, i) => (
                  <div key={i} className="border-t border-current/10 pt-2" data-testid={`tpl-botflow-diag-step-${i}`}>
                    <div className="font-mono font-semibold">
                      {s.ok ? "✓" : "✗"} Step {i + 1}: {s.name} {typeof s.http_status === "number" ? `(HTTP ${s.http_status})` : ""}
                    </div>
                    <div className="text-[10px] text-muted-foreground break-all">{s.url}</div>
                    <pre className="bg-white/60 rounded p-1.5 overflow-auto text-[10px] mt-1">{JSON.stringify(s.response, null, 2)}</pre>
                    {s.note && <div className="text-[10px] italic text-muted-foreground">{s.note}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Extra links */}
      <div>
        <Label className="text-sm">Extra links (appended at end of message)</Label>
        <div className="space-y-2 mt-2">
          {links.map((l, i) => (
            <div key={i} className="flex gap-2" data-testid={`tpl-link-${i}`}>
              <Input
                placeholder="Label (e.g. Rate us on Google)"
                value={l.label || ""}
                onChange={e => setLinks(links.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                className="flex-1"
                data-testid={`tpl-link-label-${i}`}
              />
              <Input
                placeholder="https://..."
                value={l.url || ""}
                onChange={e => setLinks(links.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                className="flex-1"
                data-testid={`tpl-link-url-${i}`}
              />
              <Button variant="ghost" size="icon" onClick={() => setLinks(links.filter((_, j) => j !== i))} data-testid={`tpl-link-del-${i}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setLinks([...links, { label: "", url: "" }])} data-testid="tpl-link-add">
            <Plus className="w-4 h-4 mr-1" />Add link
          </Button>
        </div>
      </div>

      {/* Test send dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-md" data-testid="tpl-test-dialog">
          <DialogHeader><DialogTitle>Send test — {initial.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2.5">
              Renders this template with sample values and sends via Wabis to the number below. Same code path as live sends.
            </div>
            <div>
              <Label>Recipient phone</Label>
              <Input
                placeholder="9876543210"
                value={testNum}
                onChange={e => setTestNum(e.target.value)}
                data-testid="tpl-test-recipient"
              />
            </div>
            {testResult && (
              <div className="text-xs bg-muted rounded p-2.5 font-mono space-y-1 max-h-48 overflow-auto" data-testid="tpl-test-result">
                <div>Status: <strong>{testResult.status}</strong> · Mode: {testResult.mode}</div>
                {testResult.meta?.wa_message_id && (
                  <div className="text-emerald-700 break-all">✓ wa_message_id: {testResult.meta.wa_message_id}</div>
                )}
                {testResult.meta?.media_id && (
                  <div className="text-emerald-700">✓ media_id: {testResult.meta.media_id}</div>
                )}
                {testResult.meta?.media_fallback_reason && (
                  <div className="text-rose-700">⚠ {testResult.meta.media_fallback_reason}</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={sendTest} disabled={busy} data-testid="tpl-test-send">
              <Send className="w-4 h-4 mr-1" />{busy ? "Sending…" : "Send test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------- Logs tab ---------- */
function LogsTab() {
  const [logs, setLogs] = useState([]);
  const load = () => api.get("/whatsapp/logs").then(r => setLogs(r.data));
  useEffect(() => { load(); }, []);

  return (
    <Card className="p-4" data-testid="logs-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground">{logs.length} messages logged</div>
        <Button variant="outline" size="sm" onClick={load} data-testid="logs-refresh">Refresh</Button>
      </div>
      <div className="divide-y divide-border">
        {logs.map(l => (
          <div key={l.id} className="py-3" data-testid={`wa-log-${l.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={KIND_COLOR[l.kind] || KIND_COLOR.generic}>{l.kind}</Badge>
              <Badge variant="outline">{l.status}</Badge>
              <Badge variant="outline" className="text-[10px]">{l.mode}</Badge>
              <span className="text-xs text-muted-foreground">to {l.to}</span>
              <span className="text-xs text-muted-foreground ml-auto">{new Date(l.created_at).toLocaleString()}</span>
            </div>
            <div className="text-sm mt-1 whitespace-pre-wrap line-clamp-3 text-muted-foreground">{l.body}</div>
            {l.meta?.media_id && (
              <div className="text-xs text-emerald-700 mt-1">✓ Media uploaded · id={l.meta.media_id}</div>
            )}
            {l.meta?.bot_flow_unique_id && (
              <div className="text-xs text-muted-foreground mt-1">
                Bot Flow: <code>{l.meta.bot_flow_unique_id}</code>
                {l.meta?.bot_flow_failed_step && (
                  <span className="text-rose-700 ml-2">✗ failed at step <strong>{l.meta.bot_flow_failed_step}</strong></span>
                )}
              </div>
            )}
            {l.meta?.bot_flow_fallback_reason && (
              <div className="text-xs text-rose-700 mt-1" data-testid={`wa-log-fallback-${l.id}`}>
                ↩ Bot Flow fallback reason: {l.meta.bot_flow_fallback_reason}
              </div>
            )}
            {l.meta?.error && (
              <div className="text-xs text-rose-700 mt-1">✗ {l.meta.error}</div>
            )}
          </div>
        ))}
        {logs.length === 0 && (
          <div className="py-10 text-center text-sm text-muted-foreground">No messages yet. Sends from POS, OTP, reminders and tests will appear here.</div>
        )}
      </div>
    </Card>
  );
}
